import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import HookStatusPill from './HookStatusPill';
import { hookShape } from './HookApprovalCard';
import {
    STATUS_FILTER_OPTIONS, ALL_COLUMNS, DEFAULT_VISIBLE_COLUMNS, buildHookRows, filterHookRows,
} from './hookList';
import formatRelativeDate from '../../services/formatRelativeDate';
import './hooks.css';

export const PAGE_SIZE = 50;

// v1: bump when adding new default-visible columns, so saved preferences
// from before the column existed don't hide it.
const STORAGE_KEY = 'manageHooks_visibleColumns_v1';

function loadVisibleColumns() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch { /* ignore */ }
    return DEFAULT_VISIBLE_COLUMNS;
}

const dateCell = (timestamp) => {
    const { short, full } = formatRelativeDate(timestamp);
    return <span className="hk-muted" title={full}>{short}</span>;
};

// Every hook across courses as one sortable, filterable table, with the
// row actions the approval card has (approve/revoke, edit, delete).
// Reorder and Generate stay in the by-course pane: the course name
// takes you there. Presentational — the actions come from the owner.
export default function HookListView({
    hooks, courses, categories, status, onStatusChange, busy,
    onToggleApproval, onEdit, onDelete, onSelectCourse,
}) {
    const [query, setQuery] = useState('');
    const [categoryId, setCategoryId] = useState(null);
    const [first, setFirst] = useState(0);
    const [visibleColumns, setVisibleColumns] = useState(loadVisibleColumns);
    const [showColumnToggle, setShowColumnToggle] = useState(false);
    const toggleRef = useRef(null);

    // Rows whose status changed here stay in the list until the status
    // filter changes, so a revoke can be undone where it happened.
    const [touchedIds, setTouchedIds] = useState(() => new Set());
    useEffect(() => { setTouchedIds(new Set()); setFirst(0); }, [status]);
    const touch = useCallback((hook) => setTouchedIds(prev => new Set(prev).add(hook.id)), []);

    useEffect(() => {
        if (!showColumnToggle) return undefined;
        const handler = (e) => {
            if (toggleRef.current && !toggleRef.current.contains(e.target)) setShowColumnToggle(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [showColumnToggle]);

    const toggleColumn = (key) => {
        setVisibleColumns(prev => {
            const next = prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key];
            try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    };
    const isVisible = (key) => visibleColumns.includes(key);

    const rows = useMemo(() => buildHookRows(hooks, courses), [hooks, courses]);
    const visibleRows = useMemo(
        () => filterHookRows(rows, { status, query, categoryId, touchedIds }),
        [rows, status, query, categoryId, touchedIds],
    );
    const categoryOptions = useMemo(() => categories.map(c => ({ label: c.name, value: c.id })), [categories]);

    const courseCell = (row) => (
        <button type="button" className="hk-link" onClick={() => onSelectCourse(row.course_id)} title="Open this course's hooks">
            {row.course_name}
            {row.course_status && row.course_status !== 'published' && <span className="hk-sub"> {row.course_status}</span>}
        </button>
    );

    const optionsCell = (row) => (
        <span className="hk-card-options hk-table-options">
            {row.options.map((label, index) => (
                <span
                    key={label + index}
                    className={`hk-card-option${index === row.correct_index ? ' hk-card-option-correct' : ''}`}
                    title={index === row.correct_index ? 'Correct answer' : undefined}
                >
                    {label}
                    {index === row.correct_index && <i className="pi pi-check" />}
                </span>
            ))}
        </span>
    );

    const actionsCell = (row) => {
        const approved = row.status === 'approved';
        return (
            <span className="hk-row-actions hk-table-actions">
                <button
                    type="button"
                    className={`hk-btn hk-btn-sm${approved ? '' : ' hk-btn-primary'}`}
                    disabled={busy}
                    onClick={() => { touch(row); onToggleApproval(row); }}
                >
                    <i className={`pi ${approved ? 'pi-times' : 'pi-check'}`} />
                    {approved ? 'Revoke' : 'Approve'}
                </button>
                <button type="button" className="hk-btn hk-btn-sm" disabled={busy} onClick={() => { touch(row); onEdit(row); }}>
                    <i className="pi pi-pencil" /> Edit
                </button>
                <button type="button" className="hk-btn hk-btn-sm hk-btn-danger" disabled={busy} onClick={() => onDelete(row)}>
                    <i className="pi pi-trash" /> Delete
                </button>
            </span>
        );
    };

    const header = (
        <div className="hk-toolbar">
            <div className="hk-toolbar-controls">
                <Dropdown value={status} options={STATUS_FILTER_OPTIONS} onChange={(e) => onStatusChange(e.value)} />
                <Dropdown
                    value={categoryId}
                    options={categoryOptions}
                    onChange={(e) => { setCategoryId(e.value ?? null); setFirst(0); }}
                    placeholder="All categories"
                    filter
                    showClear
                />
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        type="search"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setFirst(0); }}
                        placeholder="Search question or course"
                    />
                </span>
            </div>
            <div className="hk-toolbar-controls">
                <span className="hk-muted">{visibleRows.length} hook{visibleRows.length === 1 ? '' : 's'}</span>
                <div className="hk-col-toggle" ref={toggleRef}>
                    <button type="button" className="hk-btn hk-btn-sm" onClick={() => setShowColumnToggle(v => !v)} title="Toggle columns">
                        <i className="pi pi-cog" />
                    </button>
                    {showColumnToggle && (
                        <div className="hk-col-toggle-menu">
                            {ALL_COLUMNS.map(col => (
                                <label key={col.key} className="hk-col-toggle-item">
                                    <input
                                        type="checkbox"
                                        checked={isVisible(col.key)}
                                        disabled={col.alwaysVisible}
                                        onChange={() => toggleColumn(col.key)}
                                    />
                                    {col.label}
                                </label>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    return (
        <DataTable
            value={visibleRows}
            dataKey="id"
            header={header}
            className="hk-table"
            responsiveLayout="scroll"
            paginator={visibleRows.length > PAGE_SIZE}
            rows={PAGE_SIZE}
            first={first}
            onPage={(e) => setFirst(e.first)}
            emptyMessage={<div className="hk-empty">No hooks match.</div>}
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} hooks"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport"
        >
            <Column field="id" header="ID" sortable style={{ width: '5em' }} />
            <Column field="course_name" sortField="course_sort" header="Course" body={courseCell} sortable style={{ width: '18%' }} />
            <Column field="question" header="Question" sortable />
            {isVisible('options') && <Column field="answer" header="Options" body={optionsCell} sortable />}
            {isVisible('explanation') && <Column field="explanation" header="Explanation" body={(row) => <span className="hk-muted">{row.explanation}</span>} />}
            {isVisible('status') && (
                <Column field="status" sortField="status_sort" header="Status" body={(row) => <HookStatusPill status={row.status} />} sortable style={{ width: '7em' }} />
            )}
            {isVisible('approved') && <Column field="approved_at" header="Approved" body={(row) => dateCell(row.approved_at)} sortable style={{ width: '7em' }} />}
            {isVisible('updated') && <Column field="updated_at" header="Updated" body={(row) => dateCell(row.updated_at)} sortable style={{ width: '7em' }} />}
            <Column header="" body={actionsCell} style={{ width: '17em' }} />
        </DataTable>
    );
}

HookListView.propTypes = {
    hooks: PropTypes.arrayOf(hookShape).isRequired,
    courses: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string, status: PropTypes.string })),
    categories: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })),
    status: PropTypes.string.isRequired,
    onStatusChange: PropTypes.func.isRequired,
    busy: PropTypes.bool,
    onToggleApproval: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
    onSelectCourse: PropTypes.func.isRequired,
};

HookListView.defaultProps = { courses: [], categories: [], busy: false };
